import { FieldError, useErrorId } from '@calendly/form/parts'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { FlexLayout } from '@calendly/ui/components/flex-layout'
import { TitleText } from '@calendly/ui/components/typography'
import { nanoid } from 'nanoid'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useBooleanState } from 'hooks/useBooleanState'

import { ConfirmationPopup } from './confirmation_popup'
import { InviteeInput } from './invitee_input'
import { isInviteeDeletionStatusSuccessSelector, inviteeInvalidEmailsSelector } from './selectors'
import { deleteInvitees } from './slice'

export const InviteeSection = () => {
  const dispatch = useDispatch()
  const [inviteeList, setInviteeList] = useState([])
  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState()
  const [isFocused, focus, blur] = useBooleanState(false)
  const errorId = useErrorId({ error })

  const dismissError = useCallback(() => setError(undefined), [])
  const closeConfirmation = () => setIsConfirming(false)

  const isInviteeDeletionStatusSuccess = useSelector(isInviteeDeletionStatusSuccessSelector)
  const isInviteeEmailInvalid = useSelector(inviteeInvalidEmailsSelector)

  useEffect(() => {
    if (isInviteeDeletionStatusSuccess) {
      setInviteeList([])
      focus()
    }
  }, [focus, isInviteeDeletionStatusSuccess])

  const onClickDelete = () => {
    if (inviteeList.length === 0) {
      setError(I18n.t('errors.messages.blank'))
    } else {
      setIsConfirming(true)
    }
  }

  const confirmDeletion = () => {
    closeConfirmation()
    dispatch(deleteInvitees(inviteeList))
  }

  const buildInviteeList = (email) => ({
    id: nanoid(5),
    email,
  })

  const onInviteeAdd = (email) => (
    setInviteeList((invitee) => ([...invitee, buildInviteeList(email)]))
  )

  const onInviteeRemove = (inviteeId) => (
    setInviteeList(inviteeList.filter(({ id }) => id !== inviteeId)))

  const onInviteeUpdate = (inviteeId, newEmail) => {
    if (!newEmail) {
      onInviteeRemove(inviteeId)
      return
    }

    setInviteeList(inviteeList.map(({ id, email }) => ({
      id,
      email: id === inviteeId ? newEmail : email,
    })))
  }

  return (
    <FlexLayout direction="column" gap={24}>
      <TitleText level={3} size="x-small">
        {I18n.t('publisher.organization_settings.data_deletion.section.invitee.title')}
      </TitleText>
      <FlexLayout direction="column" gap={16}>
        <div data-testid="sub-description">
          {I18n.t('publisher.organization_settings.data_deletion.section.invitee.description')}
        </div>
        <InviteeInput
          inviteeList={inviteeList}
          onAdd={onInviteeAdd}
          onRemove={onInviteeRemove}
          onUpdate={onInviteeUpdate}
          error={error}
          invalidEmails={isInviteeEmailInvalid}
          dismissError={dismissError}
          isFocused={isFocused}
          focus={focus}
          blur={blur}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
        />
        <FieldError text={error} id={errorId} />
        <Button decoration="danger" spacingTop={16} onClick={onClickDelete}>
          {I18n.t('publisher.action.delete')}
        </Button>
        {
        isConfirming && (
          <ConfirmationPopup
            close={closeConfirmation}
            confirm={confirmDeletion}
          />
        )
      }
      </FlexLayout>
    </FlexLayout>
  )
}
